import * as React from 'react'
import {
    Container,
    Draggable,
    OnDropCallback
} from '@richardrout/react-smooth-dnd'
import { ContainerMenu } from '../container-menu'
import {
    DnDTreesProps,
    LayoutStyle,
    NodeWrapperProps,
    TreeNodeUnflattened,
    TreeNodesUnflattened
} from './dtos'
import { NodeWrapper } from './NodeWrapper'

import { DnDTreeContainer } from './hooks/useDnDTree'

import * as styles from './DnDTree.module.less'

const itemPropsAreEqual = (
    prevProps: Readonly<React.PropsWithChildren<DnDTreesProps>>,
    nextProps: Readonly<React.PropsWithChildren<DnDTreesProps>>
) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
}

export const DnDTree: React.FC<DnDTreesProps> = React.memo((props) => {
    const { actions, components, treeData, treeOptions } = props
    const [activeNode, setActiveNode] = React.useState<string | null>(null)

    const onNodeClicked = (id: string) => {
        if (props.onNodeClicked) {
            props.onNodeClicked(id)
        }
        setActiveNode(id)
    }

    return (
        <DnDTreeContainer.Provider
            initialState={{
                // @ts-ignore
                activeNode,
                actions,
                components,
                onNodeClicked,
                treeData,
                treeOptions
            }}
        >
            <DnDTreeWrapper />
        </DnDTreeContainer.Provider>
    )
}, itemPropsAreEqual)

export const DnDTreeWrapper: React.FC = () => {
    const { unflattenedTreeData } = DnDTreeContainer.useContainer().state
    const { components, modes, treeOptions } = DnDTreeContainer.useContainer()

    const Tree = buildTreeRecursively({
        treeBranch: unflattenedTreeData.nodes,
        parent: null
    })

    if (components.TreeMenu) {
        const { cardProps, Menu: TreeMenu } = components.TreeMenu
        return (
            <ContainerMenu
                menu={<TreeMenu treeOptions={treeOptions} modes={modes} />}
                cardProps={cardProps}
            >
                <div className={styles.dndTree}>
                    <div className={styles.dndTreeWrapper}>{Tree}</div>
                </div>
            </ContainerMenu>
        )
    }
    return (
        <div className={styles.dndTree}>
            <div className={styles.dndTreeWrapper}>{Tree}</div>
        </div>
    )
}

const Branch: React.FC<{
    treeBranch: TreeNodesUnflattened
    parent: TreeNodeUnflattened
}> = (props) => {
    const { treeBranch, parent } = props
    const { actions, treeOptions, modes } = DnDTreeContainer.useContainer()
    const { moveNode: moveNodeClient, setNodeDraggingId } = actions
    const { draggingId } = DnDTreeContainer.useContainer().state

    return (
        <React.Fragment>
            {treeBranch.map((node: any, index) => {
                const { id, layoutStyle } = node.data
                const { children } = node
                const parentChildrenCount = treeBranch.length
                const hasParent = parent !== undefined
                const isList = layoutStyle === LayoutStyle.List
                const isChildOfOrg = parent
                    ? parent.data.layoutStyle === LayoutStyle.Org
                    : false
                const isFirstChild = hasParent ? index === 0 : false
                const isLastChild = index === parentChildrenCount - 1
                const hasChildren = children.length > 0
                const classDragging =
                    draggingId === node.data.id ? 'isDragging' : 'isNotDragging'

                const onDropItem: OnDropCallback = (e) => {
                    if (node && e.addedIndex !== null) {
                        // console.log(`DROP ${e.payload.data.id} in ${node.data.id}`)
                        moveNodeClient({
                            index: e.addedIndex,
                            droppedNode: e.payload,
                            parentContainerNode: node
                        })
                    }
                }

                const isParentList = node
                    ? node.data.layoutStyle === LayoutStyle.List
                    : false

                const isExpanded = node?.data.id !== draggingId
                const nodeWrapperProps: NodeWrapperProps = {
                    actions,
                    treeOptions,
                    treeModes: modes.treeModes,
                    hasChildren,
                    hasParent,
                    isChildOfOrg,
                    isFirstChild,
                    isLastChild,
                    isList,
                    node,
                    expanded: isExpanded
                }

                return (
                    <Draggable
                        key={id}
                        data-id={id}
                        className={'draggable-item'}
                        data-is-dragging-node={draggingId === id}
                        data-is-leaf={!hasChildren}
                        data-is-child={hasParent}
                        data-is-child-of-org={isChildOfOrg}
                        data-is-first-child={isFirstChild}
                        data-is-last-child={isLastChild}
                        data-is-list={isList}
                        data-is-expanded={isExpanded}
                    >
                        <div className={styles.helperBorderTopLeft} />
                        <div
                            className="branch"
                            id={parent ? undefined : 'dndTree'}
                        >
                            <NodeWrapper {...nodeWrapperProps} />
                            {classDragging === 'isDragging' ? null : (
                                <div
                                    className={
                                        isParentList
                                            ? `${styles.listTreeWrapper} nodeChildrenList ${classDragging}`
                                            : `${styles.orgTreeWrapper} nodeChildrenList ${classDragging}`
                                    }
                                >
                                    <Container
                                        dragBeginDelay={300}
                                        onDragEnd={(params) =>
                                            setNodeDraggingId('')
                                        }
                                        dragHandleSelector={
                                            modes.treeModes.dragDropMode
                                                ? '.dnd-drag-handle'
                                                : 'cannot-drag'
                                        }
                                        animationDuration={650}
                                        // @ts-ignore
                                        getGhostParent={() =>
                                            document.getElementById('dndTree')
                                        }
                                        autoScrollEnabled={true}
                                        disableScrollOverlapDetection={true}
                                        dropPlaceholder={{
                                            className: styles.dropPlaceholder,
                                            animationDuration: 400
                                        }}
                                        getChildPayload={(index: number) =>
                                            children[index]
                                        }
                                        groupName={'all'}
                                        onDrop={(e) => onDropItem(e)}
                                        orientation={
                                            isParentList
                                                ? 'vertical'
                                                : 'horizontal'
                                        }
                                    >
                                        {buildTreeRecursively({
                                            parent: node,
                                            treeBranch: children
                                        })}
                                    </Container>
                                </div>
                            )}
                        </div>
                        <div className={styles.helperBorderTopRight} />
                    </Draggable>
                )
            })}
        </React.Fragment>
    )
}

const buildTreeRecursively = (args: {
    treeBranch: TreeNodesUnflattened
    parent: TreeNodeUnflattened
}) => {
    return <Branch parent={args.parent} treeBranch={args.treeBranch} />
}
